import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminNav from './AdminNav';
import './Manager.css';
import './AdminPanel.css';
import { API_BASE, getAssetUrl } from '../config/api';

const OrdersManager = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const url = statusFilter 
        ? `${API_BASE}/orders?status=${statusFilter}&limit=100`
        : `${API_BASE}/orders?limit=100`;
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setOrders(data.orders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        fetchOrders();
        if (selectedOrder && selectedOrder._id === orderId) {
          setSelectedOrder(data.data);
        }
        alert('Order status updated successfully');
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order status');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchOrders();
        if (selectedOrder && selectedOrder._id === orderId) {
          setSelectedOrder(null);
        }
        alert('Order deleted successfully');
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#faad14',
      'confirmed': '#1890ff',
      'processing': '#722ed1',
      'shipped': '#13c2c2',
      'delivered': '#52c41a',
      'cancelled': '#f5222d'
    };
    return colors[status] || '#000';
  };

  const getStatusBadge = (status) => {
    return (
      <span 
        className="status-badge" 
        style={{ 
          backgroundColor: getStatusColor(status),
          color: '#fff',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'uppercase'
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="admin-panel">
      <AdminNav />

      <div className="manager-container">
        <div className="manager-header">
          <h1>Orders Management</h1>
          <div className="header-actions">
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="">All Orders</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button className="btn-refresh" onClick={fetchOrders}>
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">Loading orders...</div>
        ) : (
          <div className="orders-layout">
            {/* Orders List */}
            <div className="orders-list">
              {orders.length === 0 ? (
                <div className="empty-state">
                  <p>No orders found</p>
                </div>
              ) : (
                <div className="orders-grid">
                  {orders.map(order => (
                    <div 
                      key={order._id} 
                      className={`order-card ${selectedOrder?._id === order._id ? 'selected' : ''}`}
                      onClick={() => setSelectedOrder(order)}
                    >
                      <div className="order-card-header">
                        <div>
                          <h3 className="order-number">
                            {order.serialNumber ? `#SL${order.serialNumber}` : `#${order.orderNumber}`}
                          </h3>
                          <p className="order-date">{formatDate(order.createdAt)}</p>
                        </div>
                        {getStatusBadge(order.status)}
                      </div>
                      <div className="order-card-body">
                        <p className="order-customer">
                          <strong>{order.customerName}</strong>
                        </p>
                        <p className="order-phone">{order.customerPhone}</p>
                        <p className="order-items">{order.items.length} item(s)</p>
                        <p className="order-total">
                          <strong>৳{order.totalAmount.toFixed(2)}</strong>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Order Details */}
            {selectedOrder && (
              <div className="order-details">
                <div className="order-details-header">
                  <h2>Order Details</h2>
                  <button 
                    className="btn-close-details"
                    onClick={() => setSelectedOrder(null)}
                  >
                    ✕
                  </button>
                </div>

                <div className="order-details-section">
                  <h3>Order Information</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Serial Number</label>
                      <p style={{ fontWeight: 700, fontSize: '18px', color: '#8B2332' }}>
                        {selectedOrder.serialNumber ? `#SL${selectedOrder.serialNumber}` : '—'}
                      </p>
                    </div>
                    <div className="detail-item">
                      <label>Order Number</label>
                      <p>{selectedOrder.orderNumber}</p>
                    </div>
                    <div className="detail-item">
                      <label>Order Date</label>
                      <p>{formatDate(selectedOrder.createdAt)}</p>
                    </div>
                    <div className="detail-item">
                      <label>Status</label>
                      <select
                        value={selectedOrder.status}
                        onChange={(e) => handleStatusChange(selectedOrder._id, e.target.value)}
                        className="status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="detail-item">
                      <label>Total Amount</label>
                      <p className="total-amount">৳{selectedOrder.totalAmount.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                <div className="order-details-section">
                  <h3>Customer Information</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Name</label>
                      <p>{selectedOrder.customerName}</p>
                    </div>
                    <div className="detail-item">
                      <label>Phone</label>
                      <p>{selectedOrder.customerPhone}</p>
                    </div>
                    <div className="detail-item full-width">
                      <label>Delivery Address</label>
                      <p>{selectedOrder.customerAddress}</p>
                    </div>
                  </div>
                </div>

                <div className="order-details-section">
                  <h3>Order Items</h3>
                  <div className="order-items-list">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="order-item">
                        <div className="order-item-image">
                          {item.image ? (
                            <img src={getAssetUrl(item.image)} alt={item.productName} />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </div>
                        <div className="order-item-details">
                          <h4>{item.productName}</h4>
                          {item.selectedColor && (
                            <p className="item-color">
                              <span
                                className="order-color-swatch"
                                style={{ backgroundColor: item.selectedColorHex || '#ccc' }}
                              />
                              Color: {item.selectedColor}
                            </p>
                          )}
                          {item.selectedSize && (
                            <p className="item-size">Size: {item.selectedSize}</p>
                          )}
                          <p className="item-quantity">Quantity: {item.quantity}</p>
                          <p className="item-price">৳{item.price.toFixed(2)} each</p>
                        </div>
                        <div className="order-item-total">
                          ৳{(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="order-details-actions">
                  <button 
                    className="btn-delete-order"
                    onClick={() => handleDeleteOrder(selectedOrder._id)}
                  >
                    Delete Order
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersManager;
